import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { InterestErrors, InterestField } from '@/lib/interest'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight, Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDictionary, useLocale } from '@/lib/i18n'
import Field, { INPUT_CLASSES } from '@/components/generals/form-field'

import {
    INTEREST_FIELDS,
    MAX_EMAIL_LENGTH,
    MAX_MESSAGE_LENGTH,
    MAX_NAME_LENGTH,
    MAX_PHONE_LENGTH,
    SIZE_OPTIONS,
    isInterestErrors,
    normalizeInterest,
    validateInterest
} from '@/lib/interest'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type Failure = 'generic' | 'rateLimited'

type FormValues = Record<InterestField | 'company', string>

const EMPTY_VALUES: FormValues = {
    name: '',
    email: '',
    phone: '',
    size: '',
    message: '',
    company: ''
}

const InterestForm: FC = (): ReactNode => {
    const t = useDictionary()
    const locale = useLocale()
    const form = t.register.form

    const [values, setValues] = useState<FormValues>(EMPTY_VALUES)
    const [errors, setErrors] = useState<InterestErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [failure, setFailure] = useState<Failure | null>(null)
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { value } = event.target

        setValues((current) => ({ ...current, [field]: value }))

        if (field !== 'company' && errors[field]) {
            setErrors((current) => {
                const next = { ...current }
                delete next[field]
                return next
            })
        }
    }

    const focusField = (field: InterestField): void => {
        const id = field === 'size' ? `interest-size-${SIZE_OPTIONS[0]}` : `interest-${field}`

        document.getElementById(id)?.focus()
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const input = normalizeInterest(values)
        const found = validateInterest(input)
        const firstInvalid = INTEREST_FIELDS.find((field) => found[field])

        setErrors(found)
        setFailure(null)

        if (firstInvalid) {
            focusField(firstInvalid)
            return
        }

        setStatus('submitting')

        try {
            const response = await fetch('/api/interest', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...input, company: values.company, locale })
            })

            if (response.ok) {
                setValues(EMPTY_VALUES)
                setStatus('success')
                return
            }

            const data: unknown = await response.json().catch(() => null)
            const fields = data && typeof data === 'object' && 'fields' in data ? (data as { fields: unknown }).fields : null

            if (response.status === 400 && isInterestErrors(fields)) setErrors(fields)

            setFailure(response.status === 429 ? 'rateLimited' : 'generic')
            setStatus('error')
        }
        catch {
            setFailure('generic')
            setStatus('error')
        }
    }

    const describedBy = (field: InterestField): string | undefined => (errors[field] ? `interest-${field}-error` : undefined)

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='rounded-3xl border border-border bg-card/85 p-8 shadow-2xl outline-none backdrop-blur sm:p-10'
            >
                <span className='flex size-12 items-center justify-center rounded-full bg-race text-white'>
                    <Check aria-hidden='true' className='size-6' />
                </span>

                <h3 className='display-heading mt-6 font-heading text-4xl font-semibold uppercase tracking-tight text-foreground'>
                    {form.successTitle}
                </h3>

                <p className='mt-3 text-base leading-relaxed text-muted-foreground'>
                    {form.successBody}
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-8 inline-flex h-11 items-center justify-center rounded-full border border-white/20 px-6 text-sm font-semibold text-foreground transition-colors hover:border-white/50 hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                >
                    {form.again}
                </button>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form
            noValidate
            onSubmit={onSubmit}
            aria-describedby='interest-consent'
            className='relative rounded-3xl border border-border bg-card/85 p-6 shadow-2xl backdrop-blur sm:p-10'
        >
            <div className='grid gap-6 sm:grid-cols-2'>
                <Field id='interest-name' label={form.name} error={errors.name ? form.errors.name : undefined} className='sm:col-span-2'>
                    <input
                        id='interest-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        required
                        maxLength={MAX_NAME_LENGTH}
                        value={values.name}
                        onChange={update('name')}
                        placeholder={form.namePlaceholder}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={describedBy('name')}
                        className={cn(INPUT_CLASSES, 'h-12')}
                    />
                </Field>

                <Field id='interest-email' label={form.email} error={errors.email ? form.errors.email : undefined}>
                    <input
                        id='interest-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        maxLength={MAX_EMAIL_LENGTH}
                        value={values.email}
                        onChange={update('email')}
                        placeholder={form.emailPlaceholder}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={describedBy('email')}
                        className={cn(INPUT_CLASSES, 'h-12')}
                    />
                </Field>

                <Field id='interest-phone' label={form.phone} hint={form.optional} error={errors.phone ? form.errors.phone : undefined}>
                    <input
                        id='interest-phone'
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        maxLength={MAX_PHONE_LENGTH}
                        value={values.phone}
                        onChange={update('phone')}
                        placeholder={form.phonePlaceholder}
                        aria-invalid={errors.phone ? true : undefined}
                        aria-describedby={describedBy('phone')}
                        className={cn(INPUT_CLASSES, 'h-12')}
                    />
                </Field>
            </div>

            <fieldset className='mt-6' aria-describedby={describedBy('size')}>
                <legend className='text-sm font-medium text-foreground'>
                    {form.size}
                </legend>

                <div className='mt-3 flex flex-wrap gap-2'>
                    {SIZE_OPTIONS.map((option) => {
                        const id = `interest-size-${option}`

                        return (
                            <div key={option}>
                                <input
                                    id={id}
                                    type='radio'
                                    name='size'
                                    value={option}
                                    checked={values.size === option}
                                    onChange={update('size')}
                                    aria-invalid={errors.size ? true : undefined}
                                    className='peer sr-only'
                                />

                                <label
                                    htmlFor={id}
                                    className={cn(
                                        'flex h-11 min-w-12 cursor-pointer select-none items-center justify-center rounded-lg border px-3.5 text-sm font-semibold tabular-nums transition-colors',
                                        'border-input text-muted-foreground hover:border-white/40 hover:text-foreground',
                                        'peer-checked:border-race peer-checked:bg-race peer-checked:text-white',
                                        'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring',
                                        errors.size && 'border-destructive'
                                    )}
                                >
                                    {option === 'not-sure' ? form.notSure : option}
                                </label>
                            </div>
                        )
                    })}
                </div>

                {errors.size && (
                    <p id='interest-size-error' className='mt-2 text-sm text-destructive'>
                        {form.errors.size}
                    </p>
                )}
            </fieldset>

            <Field
                id='interest-message'
                label={form.message}
                hint={form.optional}
                error={errors.message ? form.errors.message : undefined}
                className='mt-6'
            >
                <textarea
                    id='interest-message'
                    name='message'
                    rows={4}
                    maxLength={MAX_MESSAGE_LENGTH}
                    value={values.message}
                    onChange={update('message')}
                    placeholder={form.messagePlaceholder}
                    aria-invalid={errors.message ? true : undefined}
                    aria-describedby={describedBy('message')}
                    className={cn(INPUT_CLASSES, 'min-h-28 resize-y py-3 leading-relaxed')}
                />
            </Field>

            <div aria-hidden='true' className='absolute -left-[9999px] top-auto h-px w-px overflow-hidden'>
                <label htmlFor='interest-company'>Company</label>
                <input
                    id='interest-company'
                    name='company'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.company}
                    onChange={update('company')}
                />
            </div>

            {failure && (
                <p role='alert' className='mt-6 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground'>
                    {form.errors[failure]}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p id='interest-consent' className='max-w-xs text-xs leading-relaxed text-muted-foreground'>
                    {form.consent}
                </p>

                <button
                    type='submit'
                    disabled={submitting}
                    className='inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-race px-7 text-[15px] font-semibold text-white shadow-[0_12px_40px_-14px_oklch(0.56_0.215_25/0.8)] transition-colors hover:bg-race/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-70'
                >
                    {submitting ? (
                        <>
                            <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                            {form.submitting}
                        </>
                    ) : (
                        <>
                            {form.submit}
                            <ArrowRight aria-hidden='true' className='size-4' />
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}

export default InterestForm
